// public/js/control/controlShared.js
//
// Shared utilities and state management for the control plane: controlXY
// and o2p presets alike
//
// This module provides:
// - Shared state object
// - Common utility functions
// - localStorage and server persistence
// - CRUD operations for presets, sequences, launchers, scenes
// - Dynamic kind indexing (any kind string auto-indexes)

import { projectName } from "../system/projectContext.js";


// =============================================================
// CONSTANTS
// =============================================================

export const STORAGE_PREFIX = "oscilla_controlxy_v1"; // keep for data continuity
export const USER_STORAGE_PREFIX = "oscilla_controlxy_user_v1"; // for user-local presets
export const SAVE_DEBOUNCE_MS = 500;

// =============================================================
// SHARED STATE
// =============================================================

export const state = {
  initialized: false,
  project: null,
  items: [],

  // User-local items (separate storage, not synced to server)
  userItems: [],

  // Preset scope: "composition" (ensemble/shared) or "user" (solo/local-only)
  // Restored from localStorage if previously set; default "user" so manual fader
  // moves sync across clients out of the box.
  presetScope: (() => {
    try {
      const stored = localStorage.getItem('oscilla.presetScope');
      if (stored === 'composition' || stored === 'user') return stored;
    } catch { /* storage blocked (private mode) */ }
    return 'user';
  })(),

  // Derived views (rebuilt dynamically from items by kind)
  // Standard controlXY kinds:
  presets: {},
  sequences: {},
  launchers: {},
  configurations: {},
  // Additional kinds (o2pPresets, o2pSequences, o2pLaunchers, scenes, etc.)
  // are created dynamically by rebuildDerivedState()
};

// =============================================================
// UTILITY FUNCTIONS
// =============================================================

/**
 * Generate a unique ID
 */
export function uniqueId(prefix = "xy") {
  return (
    prefix + "_" +
    Date.now().toString(36) +
    "_" +
    Math.random().toString(36).slice(2, 10)
  );
}

/**
 * Current timestamp in milliseconds
 */
export function nowMs() {
  return Date.now();
}

/**
 * Safe JSON parse with fallback
 */
export function safeJsonParse(str, fallback) {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
}

/**
 * Clamp a number to 0-1 range
 */
export function clamp01(n) {
  if (typeof n !== "number" || !isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

// =============================================================
// PROJECT CONTEXT
// =============================================================

/** The open project's name; presets with no project are kept under "unknown_project". */
export function getProjectName() {
  return projectName("unknown_project");
}

// =============================================================
// DERIVED STATE BUILDERS
// =============================================================

// Fixed state keys that are never treated as kind buckets
const FIXED_KEYS = new Set(['initialized', 'project', 'items']);

/**
 * Pluralize a kind string for use as a state bucket key
 * "preset" -> "presets", "o2pPreset" -> "o2pPresets", "launcher" -> "launchers"
 */
function pluralizeKind(kind) {
  return kind + 's';
}

/**
 * Determine the lookup key for an item based on its kind
 * Launchers use uid, everything else uses name
 */
function itemKey(item) {
  // Any kind ending in "auncher" (launcher, o2pLauncher) keys by uid
  if (item.kind && item.kind.toLowerCase().endsWith('auncher')) {
    return item.uid;
  }
  return item.name;
}

// Fixed state keys that include user items
const USER_KEYS = new Set(['userItems', 'presetScope']);

/**
 * Rebuild derived state objects from items array
 * Dynamic: any kind string gets its own bucket automatically
 * Includes both composition and user items in derived views
 */
function rebuildDerivedState() {
  // Clear all dynamic buckets
  for (const key of Object.keys(state)) {
    if (!FIXED_KEYS.has(key) && !USER_KEYS.has(key)) {
      state[key] = {};
    }
  }

  // Add composition items
  for (const item of state.items) {
    const kind = item.kind;
    if (!kind) continue;

    const bucket = pluralizeKind(kind);
    if (!state[bucket]) state[bucket] = {};

    const key = itemKey(item);
    if (key) state[bucket][key] = item;
  }

  // Add user-local items (prefixed to avoid collisions)
  for (const item of state.userItems || []) {
    const kind = item.kind;
    if (!kind) continue;

    const bucket = pluralizeKind(kind);
    if (!state[bucket]) state[bucket] = {};

    // User items have a special marker
    const key = itemKey(item);
    if (key) {
      // Store with marker so UI can distinguish
      const userItem = { ...item, _isUserLocal: true };
      state[bucket][key] = userItem;
    }
  }
}

// =============================================================
// LOCAL STORAGE
// =============================================================

function storageKey(project) {
  return `${STORAGE_PREFIX}:${project}`;
}

/**
 * Get storage key for user-local presets
 * Includes client name to separate per-user
 */
function userStorageKey(project) {
  const clientName = window.localClientName || localStorage.getItem('clientName') || 'default';
  return `${USER_STORAGE_PREFIX}:${project}:${clientName}`;
}

/**
 * Get the current preset scope
 */
export function getPresetScope() {
  return state.presetScope;
}

/**
 * Set the preset scope ("composition" or "user")
 */
export function setPresetScope(scope) {
  if (scope !== "composition" && scope !== "user") {
    console.warn(`[controlShared] Invalid preset scope: ${scope}`);
    return;
  }
  state.presetScope = scope;
  window.presetScope = scope; // Also set on window for easy access from other modules
  try { localStorage.setItem('oscilla.presetScope', scope); } catch { /* storage blocked (private mode) */ }
  console.log(`[controlShared] Preset scope set to: ${scope}`);

  // Dispatch event for UI to update
  window.dispatchEvent(new CustomEvent('controlxy:scopeChanged', {
    detail: { scope }
  }));
}

// Initialize window.presetScope with default value
window.presetScope = state.presetScope;

/**
 * Load items from localStorage
 */
export function loadLocal(project) {
  const raw = localStorage.getItem(storageKey(project));
  const parsed = safeJsonParse(raw, null);

  if (!parsed || !Array.isArray(parsed.items)) {
    return [];
  }

  return parsed.items;
}

/**
 * Load user-local items from localStorage
 */
export function loadUserLocal(project) {
  const raw = localStorage.getItem(userStorageKey(project));
  const parsed = safeJsonParse(raw, null);

  if (!parsed || !Array.isArray(parsed.items)) {
    return [];
  }

  return parsed.items;
}

/**
 * Save items to localStorage
 */
export function saveLocal(project, items) {
  try {
    localStorage.setItem(
      storageKey(project),
      JSON.stringify({
        version: 1,
        savedAt: nowMs(),
        items
      })
    );
    return true;
  } catch (e) {
    console.warn("[controlShared] localStorage save failed:", e);
    return false;
  }
}

/**
 * Save user-local items to localStorage
 */
export function saveUserLocal(project, items) {
  try {
    localStorage.setItem(
      userStorageKey(project),
      JSON.stringify({
        version: 1,
        savedAt: nowMs(),
        items
      })
    );
    return true;
  } catch (e) {
    console.warn("[controlShared] user localStorage save failed:", e);
    return false;
  }
}

// =============================================================
// SERVER PERSISTENCE
// =============================================================

/**
 * Load presets from server (project file)
 * Returns items array or empty array if not found
 */
export async function loadFromServer(project) {
  try {
    const response = await fetch(`/api/controlxy-presets?projectId=${encodeURIComponent(project)}`);

    if (response.status === 404) {
      return [];
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();

    // Convert server format to items array
    const items = [];

    // Convert presets
    if (data.presets) {
      for (const [name, presetData] of Object.entries(data.presets)) {
        items.push({
          id: uniqueId('o2pPreset'),
          kind: 'o2pPreset',
          name,
          data: presetData,
          scope: 'local',
          createdAt: data.updatedAt || nowMs(),
          updatedAt: data.updatedAt || nowMs()
        });
      }
    }

    // Convert sequences
    if (data.sequences) {
      for (const [name, seqData] of Object.entries(data.sequences)) {
        items.push({
          id: uniqueId('o2pSequence'),
          kind: 'o2pSequence',
          name,
          data: seqData,
          scope: 'local',
          createdAt: data.updatedAt || nowMs(),
          updatedAt: data.updatedAt || nowMs()
        });
      }
    }

    // Convert launchers
    if (data.launchers) {
      for (const [uid, launcherData] of Object.entries(data.launchers)) {
        items.push({
          id: uniqueId('o2pLauncher'),
          kind: 'o2pLauncher',
          uid,
          data: launcherData,
          scope: 'local',
          createdAt: data.updatedAt || nowMs(),
          updatedAt: data.updatedAt || nowMs()
        });
      }
    }

    return items;

  } catch (err) {
    console.warn(`[controlShared] loadFromServer failed:`, err);
    return [];
  }
}

/**
 * Save presets to server (project file)
 */
export async function saveToServer(project, items) {
  try {
    // Convert items array to server format
    const presets = {};
    const sequences = {};
    const launchers = {};

    for (const item of items) {
      if (item.kind === 'o2pPreset' && item.name && item.data) {
        presets[item.name] = item.data;
      } else if (item.kind === 'o2pSequence' && item.name && item.data) {
        sequences[item.name] = item.data;
      } else if (item.kind === 'o2pLauncher' && item.uid && item.data) {
        launchers[item.uid] = item.data;
      }
    }

    // Only save if there's something to save
    if (Object.keys(presets).length === 0 &&
        Object.keys(sequences).length === 0 &&
        Object.keys(launchers).length === 0) {
      return true; // Nothing to save
    }

    const response = await fetch('/api/controlxy-presets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        projectId: project,
        presets,
        sequences,
        launchers
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return true;

  } catch (err) {
    console.warn(`[controlShared] saveToServer failed:`, err);
    return false;
  }
}

// =============================================================
// INITIALIZATION
// =============================================================

/**
 * Merge server items with local items
 * Server items take precedence for composition presets
 * Local items are kept for user-specific settings
 */
function mergeItems(serverItems, localItems) {
  // Create a map of server items by kind+name or kind+uid
  const serverMap = new Map();
  for (const item of serverItems) {
    const key = item.uid ? `${item.kind}:uid:${item.uid}` : `${item.kind}:name:${item.name}`;
    serverMap.set(key, item);
  }

  // Start with server items
  const merged = [...serverItems];

  // Add local items that don't exist on server
  for (const localItem of localItems) {
    const key = localItem.uid ? `${localItem.kind}:uid:${localItem.uid}` : `${localItem.kind}:name:${localItem.name}`;
    if (!serverMap.has(key)) {
      merged.push(localItem);
    }
  }

  return merged;
}

/**
 * Initialize the control state for a project
 * Loads from server first (composition presets), then merges with localStorage
 * Also loads user-local presets
 */
export function init(project) {
  if (!project) {
    project = getProjectName();
  }

  state.project = project;

  // First load local items synchronously for immediate availability
  const localItems = loadLocal(project);
  state.items = localItems;

  // Load user-local items
  const userLocalItems = loadUserLocal(project);
  state.userItems = userLocalItems;

  rebuildDerivedState();
  state.initialized = true;

  // Then load from server asynchronously and merge
  loadFromServer(project).then(serverItems => {
    // another project opened while this answer was on its way: it is not theirs
    if (state.project !== project) return;
    if (serverItems.length > 0) {
      // Merge server items with local items
      state.items = mergeItems(serverItems, localItems);
      rebuildDerivedState();

      // Save merged state to localStorage
      saveLocal(project, state.items);

      // Dispatch event to refresh UI
      window.dispatchEvent(new CustomEvent('controlxy:serverLoaded', {
        detail: { project, serverItemCount: serverItems.length }
      }));
    }
  });

  // Dispatch loaded event
  window.dispatchEvent(new CustomEvent('controlxy:loaded', {
    detail: {
      project,
      presetCount: Object.keys(state.presets || {}).length,
      sequenceCount: Object.keys(state.sequences || {}).length,
      configCount: Object.keys(state.configurations || {}).length
    }
  }));

  return state;
}

// =============================================================
// SAVE (debounced)
// =============================================================

let saveDebounceTimer = null;
let serverSaveDebounceTimer = null;
const SERVER_SAVE_DEBOUNCE_MS = 2000; // Longer debounce for server saves

/**
 * Save current state to localStorage and server (debounced)
 */
export function save() {
  if (!state.project) return;

  // Local save (fast, short debounce)
  if (saveDebounceTimer) {
    clearTimeout(saveDebounceTimer);
  }

  saveDebounceTimer = setTimeout(() => {
    // Save composition items
    const success = saveLocal(state.project, state.items);

    // Also save user items
    saveUserLocal(state.project, state.userItems || []);

    if (success) {
      window.dispatchEvent(new CustomEvent('controlxy:saved', {
        detail: {
          project: state.project,
          itemCount: state.items.length,
          userItemCount: (state.userItems || []).length
        }
      }));
    }
  }, SAVE_DEBOUNCE_MS);

  // Server save (slower, longer debounce to batch multiple changes)
  // NOTE: Only composition items are synced to server, not user items
  if (serverSaveDebounceTimer) {
    clearTimeout(serverSaveDebounceTimer);
  }

  serverSaveDebounceTimer = setTimeout(() => {
    saveToServer(state.project, state.items);
  }, SERVER_SAVE_DEBOUNCE_MS);
}

/**
 * Force immediate save (bypasses debounce)
 */
export function forceSave() {
  if (saveDebounceTimer) {
    clearTimeout(saveDebounceTimer);
    saveDebounceTimer = null;
  }
  if (serverSaveDebounceTimer) {
    clearTimeout(serverSaveDebounceTimer);
    serverSaveDebounceTimer = null;
  }

  if (!state.project) return false;

  const localSuccess = saveLocal(state.project, state.items);

  // Also save to server
  saveToServer(state.project, state.items);

  return localSuccess;
}

// =============================================================
// CRUD OPERATIONS
// =============================================================

// Render callback - set by UI modules
let renderCallback = null;

export function setRenderCallback(callback) {
  renderCallback = callback;
}

function triggerRender() {
  if (typeof renderCallback === "function") {
    renderCallback();
  }
}

/**
 * Add a new item
 * If presetScope is "user", adds to userItems (local-only)
 * If presetScope is "composition", adds to items (shared)
 */
export function addItem(item, forceScope = null) {
  if (!item.id) item.id = uniqueId(item.kind || "item");
  if (!item.createdAt) item.createdAt = nowMs();
  if (!item.updatedAt) item.updatedAt = nowMs();

  // Determine which list to add to based on scope
  const scope = forceScope || state.presetScope;
  const isUserLocal = scope === "user";

  if (isUserLocal) {
    if (!state.userItems) state.userItems = [];
    item._isUserLocal = true;
    state.userItems.push(item);
  } else {
    state.items.push(item);
  }

  rebuildDerivedState();
  save();
  triggerRender();
  return item;
}

/**
 * Update an existing item by ID
 * Searches both composition and user items
 */
export function updateItem(id, patch) {
  // Check composition items first
  let idx = state.items.findIndex(x => x.id === id);
  let isUserLocal = false;

  if (idx < 0) {
    // Check user items
    idx = (state.userItems || []).findIndex(x => x.id === id);
    isUserLocal = true;
  }

  if (idx < 0) return null;

  const itemList = isUserLocal ? state.userItems : state.items;
  const prev = itemList[idx];
  const next = {
    ...prev,
    ...patch,
    updatedAt: nowMs()
  };

  itemList[idx] = next;
  rebuildDerivedState();
  save();
  triggerRender();
  return next;
}

/**
 * Delete an item by ID
 * Searches both composition and user items
 */
export function deleteItem(id) {
  // Check composition items first
  let idx = state.items.findIndex(x => x.id === id);
  let isUserLocal = false;

  if (idx < 0) {
    // Check user items
    idx = (state.userItems || []).findIndex(x => x.id === id);
    isUserLocal = true;
  }

  if (idx < 0) return false;

  const itemList = isUserLocal ? state.userItems : state.items;
  itemList.splice(idx, 1);
  rebuildDerivedState();
  save();
  triggerRender();
  return true;
}

// =============================================================
// PRESET HELPERS (controlXY backward compat)
// =============================================================

/**
 * Get preset by name
 */
export function getPreset(name) {
  return state.presets?.[name] || null;
}

/**
 * List all preset names
 */
export function listPresets() {
  return Object.keys(state.presets || {});
}

/**
 * Save/update a preset
 */
export function savePreset(name, data) {
  const existing = state.presets?.[name];
  
  if (existing) {
    return updateItem(existing.id, { data });
  } else {
    return addItem({
      kind: "preset",
      name,
      data,
      scope: "local"
    });
  }
}

/**
 * Delete a preset by name
 */
export function deletePreset(name) {
  const item = state.presets?.[name];
  if (!item) return false;
  return deleteItem(item.id);
}

// =============================================================
// SEQUENCE HELPERS
// =============================================================

/**
 * Get sequence by name
 */
export function getSequence(name) {
  return state.sequences?.[name] || null;
}

/**
 * List all sequence names
 */
export function listSequences() {
  return Object.keys(state.sequences || {});
}

/**
 * Save/update a sequence
 */
export function saveSequence(name, steps, options = {}) {
  const existing = state.sequences?.[name];
  
  const data = {
    steps,
    loop: options.loop ?? false
  };
  
  if (existing) {
    return updateItem(existing.id, { data });
  } else {
    return addItem({
      kind: "sequence",
      name,
      data,
      scope: "local"
    });
  }
}

/**
 * Delete a sequence by name
 */
export function deleteSequence(name) {
  const item = state.sequences?.[name];
  if (!item) return false;
  return deleteItem(item.id);
}

// =============================================================
// LAUNCHER HELPERS
// =============================================================

/**
 * Get launcher state by UID
 */
export function getLauncher(uid) {
  return state.launchers?.[uid] || null;
}

/**
 * List all launcher UIDs
 */
export function listLaunchers() {
  return Object.keys(state.launchers || {});
}

/**
 * Save/update launcher state
 */
export function saveLauncher(uid, launcherData) {
  const existing = state.launchers?.[uid];
  
  if (existing) {
    return updateItem(existing.id, { data: launcherData });
  } else {
    return addItem({
      kind: "launcher",
      uid,
      data: launcherData,
      scope: "local"
    });
  }
}

/**
 * Get or create launcher state
 */
export function getOrCreateLauncher(uid, defaults = {}) {
  const existing = state.launchers?.[uid];
  
  if (existing && existing.data) {
    return existing.data;
  }
  
  const launcherData = {
    currentBank: 0,
    mode: 'preset',
    tween: true,
    visible: true,
    banks: [],
    ...defaults
  };
  
  saveLauncher(uid, launcherData);
  
  return launcherData;
}

// =============================================================
// GENERIC KIND HELPERS (for o2p and future controllers)
// =============================================================

/**
 * Get item from any kind bucket by name
 * Usage: getByKind("o2pPreset", "warm_mix")
 */
export function getByKind(kind, name) {
  const bucket = pluralizeKind(kind);
  return state[bucket]?.[name] || null;
}

/**
 * List all names in a kind bucket
 * Usage: listByKind("o2pPreset") -> ["warm_mix", "dark_mix"]
 */
export function listByKind(kind) {
  const bucket = pluralizeKind(kind);
  return Object.keys(state[bucket] || {});
}

/**
 * Save/update an item in any kind bucket
 * Usage: saveByKind("o2pPreset", "warm_mix", data)
 */
export function saveByKind(kind, name, data) {
  const existing = getByKind(kind, name);
  
  if (existing) {
    return updateItem(existing.id, { data });
  } else {
    return addItem({
      kind,
      name,
      data,
      scope: "local"
    });
  }
}

/**
 * Delete an item from any kind bucket
 */
export function deleteByKind(kind, name) {
  const item = getByKind(kind, name);
  if (!item) return false;
  return deleteItem(item.id);
}

/**
 * Save/update a launcher-style item (keyed by uid)
 */
export function saveLauncherByKind(kind, uid, data) {
  const bucket = pluralizeKind(kind);
  const existing = state[bucket]?.[uid];
  
  if (existing) {
    return updateItem(existing.id, { data });
  } else {
    return addItem({
      kind,
      uid,
      data,
      scope: "local"
    });
  }
}

/**
 * Get or create a launcher-style item
 */
export function getOrCreateLauncherByKind(kind, uid, defaults = {}) {
  const bucket = pluralizeKind(kind);
  const existing = state[bucket]?.[uid];
  
  if (existing && existing.data) {
    return existing.data;
  }
  
  const data = { ...defaults };
  saveLauncherByKind(kind, uid, data);
  return data;
}

// =============================================================
// EXPORT / IMPORT
// =============================================================

export function exportData() {
  return {
    version: 1,
    exportedAt: nowMs(),
    project: state.project,
    // the composition's items and the performer's own (user scope, the default)
    items: [...state.items, ...(state.userItems || [])]
  };
}

export function importData(data, options = {}) {
  const { merge = false } = options;
  
  if (!data || !Array.isArray(data.items)) {
    console.warn("[controlShared] Invalid import data");
    return false;
  }
  
  if (merge) {
    for (const item of data.items) {
      const exists = state.items.some(x => x.id === item.id);
      if (!exists) {
        state.items.push(item);
      }
    }
  } else {
    state.items = data.items;
  }
  
  rebuildDerivedState();
  forceSave();
  triggerRender();
  
  window.dispatchEvent(new CustomEvent('controlxy:launcherRefresh', {
    detail: {}
  }));

  return true;
}

// =============================================================
// GLOBAL API
// =============================================================

window.controlXYState = state;

export default {
  STORAGE_PREFIX,
  USER_STORAGE_PREFIX,
  state,

  // Utilities
  uniqueId, nowMs, safeJsonParse, clamp01, getProjectName,

  // Init
  init,

  // Preset scope (user vs composition)
  getPresetScope, setPresetScope,

  // Persistence
  save, forceSave, loadLocal, saveLocal, loadUserLocal, saveUserLocal,

  // CRUD
  setRenderCallback, addItem, updateItem, deleteItem,

  // controlXY presets (backward compat)
  getPreset, listPresets, savePreset, deletePreset,

  // Sequences
  getSequence, listSequences, saveSequence, deleteSequence,

  // Launchers
  getLauncher, listLaunchers, saveLauncher, getOrCreateLauncher,


  // Generic kind helpers
  getByKind, listByKind, saveByKind, deleteByKind,
  saveLauncherByKind, getOrCreateLauncherByKind,

  // Export/Import
  exportData, importData,
};
